import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiMail, FiArrowRight, FiUsers, FiSend, FiX } from 'react-icons/fi';
import MegaphoneIcon from '../../MegaphoneIcon';
import Toggle from '../../Toggle';
import Dropdown from '../../Dropdown';
import { toast } from '../../Toast';
import { IGlobalState } from '../../../../model/IGlobalState';
import { ASX_TEMPLATES, WELCOME_TEMPLATES, SEND_WHEN, TEST_ANNOUNCEMENTS } from './announcementData';
import { SectionProps } from './AnnouncementSettings';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type TestEmail = 'asx' | 'welcome';

// the two system emails; "Edit Design" / "Open the Email Builder" go to the existing v2 Email Templates page
const Emails: React.FC<SectionProps> = ({ form, setForm }) => {
    const history = useHistory();
    const user = useSelector((state: IGlobalState) => state.user);
    const [testEmail, setTestEmail] = useState<TestEmail>('asx');
    const [testAnnouncement, setTestAnnouncement] = useState(TEST_ANNOUNCEMENTS[0].value);
    const [newAddress, setNewAddress] = useState('');

    const setAsx = (field: keyof typeof form.asx, value: string | boolean) => setForm(f => ({ ...f, asx: { ...f.asx, [field]: value } }));
    const setWelcome = (field: keyof typeof form.welcome, value: string | boolean) => setForm(f => ({ ...f, welcome: { ...f.welcome, [field]: value } }));
    const templates = () => history.push('/communications/email-templates');
    const editDesign = <button type="button" className="link" onClick={templates}>Edit Design <FiArrowRight /></button>;

    const me = user?.username ?? '';
    // the extra test addresses are a saved setting (Save Changes), like the rest of the page
    const addAddress = () => {
        const a = newAddress.trim();
        if (!a) return;
        if (!EMAIL.test(a)) {
            toast.error('Type a full email address');
            return;
        }
        if (a.toLowerCase() !== me.toLowerCase() && !form.testEmails.some(x => x.toLowerCase() === a.toLowerCase())) {
            setForm(f => ({ ...f, testEmails: [...f.testEmails, a] }));
        }
        setNewAddress('');
    };

    const sendTest = () => {
        const count = form.testEmails.length + 1;
        const which = testEmail === 'asx' ? 'announcement email' : 'welcome email';
        toast.info(`Preview only: this isn’t connected yet, so the test ${which} wasn’t sent to the ${count} ${count === 1 ? 'address' : 'addresses'}`);
    };

    return <>
        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiMail /></span>
                <div className="text">
                    <h2>System Emails</h2>
                    <div className="hint">Two emails Investi sends on its own. Both are built in the email builder — no HTML.</div>
                </div>
                <button type="button" className="link in-title" onClick={templates}>Open the Email Builder <FiArrowRight /></button>
            </div>

            <div className="system-email">
                <div className="head">
                    <span className="icon-tile small"><MegaphoneIcon /></span>
                    <div className="text">
                        <strong>ASX announcement email</strong>
                        <span>Sent for every announcement — instantly if price‑sensitive, after approval if not</span>
                    </div>
                    <span className="badge info">Sent automatically</span>
                    {editDesign}
                </div>
                <div className="fields">
                    <div className="field">
                        <span className="label">Template</span>
                        <Dropdown value={form.asx.template} options={ASX_TEMPLATES} onChange={v => setAsx('template', v)} />
                    </div>
                    <label className="field">
                        <span className="label">Subject line</span>
                        <input value={form.asx.subject} onChange={e => setAsx('subject', e.target.value)} />
                    </label>
                </div>
            </div>

            <div className="system-email">
                <div className="head">
                    <span className="icon-tile small"><FiUsers /></span>
                    <div className="text">
                        <strong>Welcome email</strong>
                        <span>Sent once when someone signs up on the Website Hub</span>
                    </div>
                    <span className={`badge ${form.welcome.send ? 'info' : 'off'}`}>{form.welcome.send ? 'Sent automatically' : 'Turned off'}</span>
                    {editDesign}
                </div>
                <div className="fields">
                    <div className="field">
                        <span className="label">Template</span>
                        <Dropdown value={form.welcome.template} options={WELCOME_TEMPLATES} onChange={v => setWelcome('template', v)} />
                    </div>
                    <label className="field">
                        <span className="label">Subject line</span>
                        <input value={form.welcome.subject} onChange={e => setWelcome('subject', e.target.value)} />
                    </label>
                </div>
                <div className="field">
                    <span className="label">When to send</span>
                    <Dropdown value={form.welcome.when} options={SEND_WHEN} onChange={v => setWelcome('when', v)} disabled={!form.welcome.send} />
                </div>
                <div className="setting-row plain divided">
                    <div className="text">
                        <div className="title">Send the welcome email</div>
                        <div className="hint">Turn off and new sign‑ups get nothing until your next campaign</div>
                    </div>
                    <Toggle label="Send the welcome email" checked={form.welcome.send} onChange={v => setWelcome('send', v)} />
                </div>
            </div>
            <button type="button" className="link at-bottom" onClick={templates}>Open the Email Builder <FiArrowRight /></button>
        </section>

        <section className="card">
            <div className="section-title"><span className="icon-tile"><FiSend /></span><h2>Send a Test</h2></div>
            <div className="field">
                <span className="label">Which email</span>
                <div className="segmented" role="radiogroup" aria-label="Which email to test">
                    <button type="button" role="radio" aria-checked={testEmail === 'asx'} className={testEmail === 'asx' ? 'active' : ''} onClick={() => setTestEmail('asx')}><MegaphoneIcon /> Announcement email</button>
                    <button type="button" role="radio" aria-checked={testEmail === 'welcome'} className={testEmail === 'welcome' ? 'active' : ''} onClick={() => setTestEmail('welcome')}><FiUsers /> Welcome email</button>
                </div>
            </div>
            {testEmail === 'asx' && <div className="field">
                <span className="label">Announcement</span>
                <Dropdown value={testAnnouncement} options={TEST_ANNOUNCEMENTS} onChange={setTestAnnouncement} />
            </div>}
            <div className="field">
                <span className="label">Send the test to</span>
                <div className="tag-input">
                    <div className="tags">
                        <span className="tag"><span className="who">{me} (you)</span></span>
                        {form.testEmails.map(a => <span key={a} className="tag">
                            <span className="who">{a}</span>
                            <button type="button" aria-label={`Remove ${a}`} onClick={() => setForm(f => ({ ...f, testEmails: f.testEmails.filter(x => x !== a) }))}><FiX /></button>
                        </span>)}
                    </div>
                    <input type="email" value={newAddress} placeholder="Add another email address…" onChange={e => setNewAddress(e.target.value)}
                        onBlur={() => { if (newAddress.trim()) addAddress(); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addAddress(); } }} />
                </div>
            </div>
            <p className="note">Test emails only go to these addresses, never to subscribers. Extra addresses are kept for next time once you Save Changes.</p>
            <button type="button" className="btn-dark" onClick={sendTest}>
                <FiSend /> Send Test {testEmail === 'asx' ? 'Announcement Email' : 'Welcome Email'}
            </button>
        </section>
    </>;
};

export default Emails;
